import { Link } from 'react-router-dom';
import { MapPin, Calendar, Users, ArrowRight, Clock } from 'lucide-react';

const TripCard = ({ 
  trip, 
  variant = 'default', // 'default', 'carousel', 'compact'
  showButton = false,
  buttonText = 'Book Now',
  showPhotoCount = true,
  showUsers = true,
  showCalendar = true,
  showClock = false,
  priceLabel = 'for 2 nights',
  priceSuffix = '',
  className = ''
}) => {
  const startDate = trip.startDate?.toDate ? trip.startDate.toDate() : new Date(trip.startDate);
  const endDate = trip.endDate?.toDate ? trip.endDate.toDate() : new Date(trip.endDate);
  const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  const firstPhoto = trip.photos && trip.photos.length > 0 ? trip.photos[0] : null;
  
  // Calculate pricing
  const pricingModel = trip.pricingModel || 'base';
  const dailyPrice = trip.dailyPrice || 0;
  const basePrice = trip.basePrice || trip.price || 0;
  
  // Calculate 2-night total
  const calculateTwoNightTotal = () => {
    if (pricingModel === 'daily' && dailyPrice) {
      return dailyPrice * 2;
    }
    return basePrice;
  };
  
  const twoNightTotal = calculateTwoNightTotal();

  // Base classes for different variants
  const cardClasses = {
    default: 'group bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200/50 hover:border-emerald-200',
    carousel: 'flex-shrink-0 w-72 bg-white rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all overflow-hidden group',
    compact: 'group bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all border border-gray-200/50'
  };

  const imageHeight = {
    default: 'h-64',
    carousel: 'h-36',
    compact: 'h-48'
  };

  const paddingClasses = {
    default: 'p-6',
    carousel: 'p-4',
    compact: 'p-4'
  };

  return (
    <Link
      to={`/trips/${trip.id}`}
      className={`${cardClasses[variant]} ${className}`}
    >
      {/* Image */}
      <div className={`relative ${imageHeight[variant]} overflow-hidden bg-gradient-to-br from-emerald-100 to-green-100`}>
        {firstPhoto ? (
          <img
            src={firstPhoto}
            alt={trip.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {variant === 'carousel' ? (
              <span className="text-4xl">🌿</span>
            ) : (
              <MapPin className="w-20 h-20 text-emerald-600/30" />
            )}
          </div>
        )}
        {showPhotoCount && trip.photos && trip.photos.length > 1 && (
          <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
            {trip.photos.length} photos
          </div>
        )}
      </div>

      {/* Content */}
      <div className={paddingClasses[variant]}>
        <h3 className={`${variant === 'default' ? 'text-xl font-medium' : 'text-lg font-semibold'} text-gray-900 mb-2 line-clamp-2 group-hover:text-emerald-700 transition-colors`}>
          {trip.title}
        </h3>
        
        <div className={`${variant === 'default' ? 'space-y-2 mb-4' : 'space-y-1.5 mb-3'}`}>
          <div className="flex items-center gap-2 text-gray-600">
            <MapPin className={`w-4 h-4 ${variant === 'default' ? 'text-emerald-600' : 'text-red-400'} flex-shrink-0`} />
            <span className={`text-sm ${variant === 'default' ? 'font-light line-clamp-1' : ''} ${variant === 'carousel' ? 'truncate' : ''}`}>
              {trip.location}
            </span>
          </div>
          
          {showCalendar && (
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className={`w-4 h-4 ${variant === 'default' ? 'text-emerald-600' : 'text-gray-500'} flex-shrink-0`} />
              <span className={`text-sm ${variant === 'default' ? 'font-light' : ''}`}>
                {daysDiff} {daysDiff === 1 ? 'day' : 'days'}
              </span>
            </div>
          )}
          
          {showClock && (
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="text-sm">{daysDiff} {daysDiff === 1 ? 'day' : 'days'}</span>
            </div>
          )}
          
          {showUsers && (
            <div className="flex items-center gap-2 text-gray-600">
              <Users className={`w-4 h-4 ${variant === 'default' ? 'text-emerald-600' : 'text-gray-500'} flex-shrink-0`} />
              <span className={`text-sm ${variant === 'default' ? 'font-light' : ''}`}>
                {trip.capacity} {trip.capacity === 1 ? 'person' : 'people'}
              </span>
            </div>
          )}
        </div>

        {showButton ? (
          <div className="mb-3">
            <span className={`${variant === 'default' ? 'text-2xl' : 'text-xl'} font-semibold ${variant === 'default' ? 'text-emerald-700' : 'text-gray-900'}`}>
              ${twoNightTotal}
            </span>
            {priceSuffix && (
              <span className="text-sm text-gray-600 ml-1">{priceSuffix}</span>
            )}
          </div>
        ) : (
          <div className={`flex items-center justify-between ${variant === 'default' ? 'pt-4 border-t border-gray-200' : ''}`}>
            <div>
              <span className={`${variant === 'default' ? 'text-2xl' : 'text-xl'} font-semibold ${variant === 'default' ? 'text-emerald-700' : 'text-gray-900'}`}>
                ${twoNightTotal}
              </span>
              {priceLabel && (
                <span className={`text-sm ${variant === 'default' ? 'text-gray-500' : 'text-gray-600'} ml-1`}>
                  {priceLabel}
                </span>
              )}
              {priceSuffix && (
                <span className="text-sm text-gray-600 ml-1">{priceSuffix}</span>
              )}
            </div>
            {variant === 'default' && (
              <ArrowRight className="w-5 h-5 text-emerald-600 group-hover:translate-x-1 transition-transform" />
            )}
          </div>
        )}

        {showButton && (
          <button 
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-2.5 rounded-lg font-medium text-sm transition-colors"
            onClick={(e) => e.preventDefault()}
          >
            {buttonText}
          </button>
        )}
      </div>
    </Link>
  );
};

export default TripCard;

